import Service from '@ember/service';
import { QueryManager } from '@apollo/client/core/QueryManager';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { queryManager } from 'ember-apollo-client';
import studentsQuery from 'dashboard/gql/queries/students.gql';
import { escapeHtml } from 'dashboard/utils/escape-html';
import {
  EXPORT_DEFAULT_ORDER_BY,
  EXPORT_PAGE_SIZE,
  exportLastActive,
  exportStatusLabel,
  fetchAllExportPages,
  showCsvDownloadToast,
} from 'dashboard/utils/export-list';
import { type IntlService } from 'ember-intl';
import type { School, Student } from 'dashboard/types';
import type ToastService from 'dashboard/services/toast';
import type { StudentListModel } from 'dashboard/controllers/dashboard/account/students';

type StudentsQueryResult = { students: StudentListModel['students'] };

export default class ExportStudentsService extends Service {
  @queryManager declare apollo: QueryManager<Service>;
  @service declare intl: IntlService;
  @service declare toast: ToastService;

  startExport(
    purchaser: School,
    currentStatuses: Array<string>,
    currentYearGroup: string | undefined,
  ) {
    if (!purchaser) return;
    return this.exportStudentsTask.perform(
      purchaser,
      currentStatuses,
      currentYearGroup,
    );
  }

  exportStudentsTask = task(
    { drop: true },
    async (
      purchaser: School,
      currentStatuses: Array<string>,
      currentYearGroup: string | undefined,
    ) => {
      const searchParams: {
        purchaserId: string;
        orderBy: string;
        accountStatuses?: string[];
        yearGroup?: string[];
      } = {
        purchaserId: purchaser.id,
        orderBy: EXPORT_DEFAULT_ORDER_BY,
      };

      if (currentStatuses.length > 0) {
        searchParams.accountStatuses = currentStatuses;
      }

      if (currentYearGroup) searchParams.yearGroup = [currentYearGroup];

      const allStudents = await fetchAllExportPages<Student>({
        toast: this.toast,
        fetchPage: async (after) => {
          // @ts-expect-error how to type apollo query responses?
          const result: StudentsQueryResult = await this.apollo.query({
            query: studentsQuery,
            variables: { first: EXPORT_PAGE_SIZE, after, searchParams },
            fetchPolicy: 'network-only',
          });
          return result.students;
        },
        messages: {
          start: this.intl.t('Exporting students…'),
          progress: (count, total) =>
            this.intl.t('Exporting students… {count} of {total}', {
              count,
              total,
            }),
          error: 'Error exporting students.',
        },
      });

      // cancelled
      if (!allStudents) return;

      // do not export CSV if there are no students.
      if (allStudents.length === 0) {
        const hasFilters = currentStatuses.length > 0 || currentYearGroup;
        this.toast.notify(
          this.intl.t(
            hasFilters
              ? 'There are no students with the selected filters'
              : 'There are no students in this school',
          ),
          'neutral',
        );
        return;
      }

      this.exportCsv(allStudents, purchaser);
    },
  );

  exportCsv(students: Student[], purchaser: School) {
    const header = [
      this.intl.t('First Name'),
      this.intl.t('Last Name'),
      this.intl.t('Username'),
      this.intl.t('Email'),
      this.intl.t('Year Group'),
      this.intl.t('Last active'),
      this.intl.t('Status'),
    ];
    const rows = students.map((student) => this.#buildRow(student));

    // escape the school name since it's directly inserted, just in case
    const safeName = escapeHtml(purchaser.name);

    showCsvDownloadToast(
      [header, ...rows],
      `${safeName} - Students.csv`,
      this.intl.t('{name} - Students.csv', { name: safeName }),
    );
  }

  #buildRow(student: Student): string[] {
    return [
      student.firstName ?? '',
      student.lastName ?? '',
      student.username ?? '',
      student.email ?? '',
      student.yearGroup ?? '',
      exportLastActive(this.intl, student),
      exportStatusLabel(this.intl, student),
    ];
  }
}

declare module '@ember/service' {
  interface Registry {
    'export-students': ExportStudentsService;
  }
}
